import { IntegrationError } from "chatkit-web-shared/errors"
import { decodeBase64 } from "chatkit-web-shared/frameEncoding"

import { ChatKitElementBase } from "../../chatkit-web-outer/src/ChatKitElementBase"
import { createChatKitOptions } from "./helpers"

import type { ChatKitShellOptions, ConfigurableChatKitOptions } from "./types"

export class ChatGPTShellElement extends ChatKitElementBase<ChatKitShellOptions> {
  #fetchHeaders?: Record<string, string>
  #auth?: ChatKitShellOptions["auth"]
  #configurableChatKitOptions?: Readonly<ConfigurableChatKitOptions>

  constructor() {
    super({ profile: "chatgpt-shell" })
  }

  override setOptions(options: ChatKitShellOptions) {
    // Swap out profile to a more limited capability profile for anonymous users
    // on first setOptions call.
    if (
      !this.#auth &&
      typeof options.auth === "object" &&
      !("userToken" in options.auth && !!options.auth.userToken)
    ) {
      this.setProfile("chatgpt-shell-anonymous")
    }
    super.setOptions(options)
  }

  // Manually validate ChatGPT shell-specific option values.
  // Other options will be validated by the inner iframe.
  #validateOptions(options: unknown): ChatKitShellOptions {
    if (typeof options !== "object" || options === null) {
      throw new IntegrationError("Invalid options: must be an object")
    }

    if (
      !("auth" in options) ||
      typeof options.auth !== "object" ||
      options.auth === null ||
      !("anonToken" in options.auth)
    ) {
      throw new IntegrationError(
        "Invalid options: auth must be an object with an anonToken property",
      )
    }

    if (
      !("mcpServer" in options) ||
      typeof options.mcpServer !== "object" ||
      options.mcpServer === null ||
      !("url" in options.mcpServer) ||
      !("name" in options.mcpServer) ||
      !("authorization" in options.mcpServer)
    ) {
      throw new IntegrationError(
        "Invalid options: mcpServer must be an object with a url, name, and authorization property",
      )
    }

    if ("fetchHeaders" in options && typeof options.fetchHeaders !== "object") {
      throw new IntegrationError("Invalid options: fetchHeaders must be an object when provided")
    }

    return {
      ...options,
      auth: options.auth,
      mcpServer: options.mcpServer,
      fetchHeaders: "fetchHeaders" in options ? options.fetchHeaders : undefined,
    } as ChatKitShellOptions
  }

  #getUserTokenIdentity(userToken: string) {
    const [header, payload, signature, ...extraSegments] = userToken.split(".")

    if (!header || !payload || !signature || extraSegments.length > 0) {
      throw new IntegrationError("Invalid options: auth.userToken must be a valid JWT")
    }

    try {
      const claims = decodeBase64<Record<string, unknown>>(payload)

      if (!claims || typeof claims !== "object" || typeof claims.sub !== "string" || !claims.sub) {
        throw new Error("Missing token subject")
      }

      const issuer = claims.iss
      if (issuer !== undefined && (typeof issuer !== "string" || !issuer)) {
        throw new Error("Invalid token issuer")
      }

      const authClaims = claims["https://api.openai.com/auth"]
      const accountId =
        authClaims && typeof authClaims === "object" && "chatgpt_account_id" in authClaims
          ? authClaims.chatgpt_account_id
          : undefined

      if (accountId !== undefined && (typeof accountId !== "string" || !accountId)) {
        throw new Error("Invalid token account")
      }

      return {
        subject: claims.sub,
        issuer,
        accountId,
      }
    } catch {
      throw new IntegrationError("Invalid options: auth.userToken must be a valid JWT")
    }
  }

  override sanitizeOptions(options: ChatKitShellOptions) {
    const validatedOptions = this.#validateOptions(options)

    const {
      auth,
      mcpServer,
      fetchHeaders,
      initialThread,
      startScreen,
      threadItemActions,
      theme,
      composer,
      widgets,
      features,
    } = validatedOptions

    if (!this.#auth) {
      this.#auth = { ...auth }
    } else if (this.profile === "chatgpt-shell-anonymous") {
      delete this.#auth.userToken
    } else if (this.#auth.userToken && auth.userToken && this.#auth.userToken !== auth.userToken) {
      if (this.#auth.anonToken !== auth.anonToken) {
        throw new IntegrationError(
          "Cannot change the authentication session of an existing ChatGPT Shell",
        )
      }

      const currentIdentity = this.#getUserTokenIdentity(this.#auth.userToken)
      const nextIdentity = this.#getUserTokenIdentity(auth.userToken)

      if (
        currentIdentity.subject !== nextIdentity.subject ||
        currentIdentity.accountId !== nextIdentity.accountId ||
        currentIdentity.issuer !== nextIdentity.issuer
      ) {
        throw new IntegrationError(
          "Cannot change the authenticated user of an existing ChatGPT Shell",
        )
      }

      this.#auth = { ...this.#auth, userToken: auth.userToken }
    }

    this.#fetchHeaders ??= {
      ...fetchHeaders,
      ...(mcpServer && {
        "x-mcp-server-url": mcpServer.url,
        "x-mcp-server-name": mcpServer.name,
        "x-mcp-server-authorization": mcpServer.authorization,
        "x-mcp-server-description": mcpServer.description ?? "",
        "x-mcp-server-agent": mcpServer.agent ?? "",
      }),
    }

    this.#configurableChatKitOptions = {
      ...this.#configurableChatKitOptions,
      initialThread,
      startScreen,
      threadItemActions: {
        share: threadItemActions?.share,
      },
      theme,
      composer: {
        placeholder: composer?.placeholder,
        tools: composer?.tools,
      },
      widgets,
    }

    return createChatKitOptions({
      auth: this.#auth!,
      fetchHeaders: this.#fetchHeaders!,
      features,
      ...this.#configurableChatKitOptions!,
    })
  }
}

export function registerChatGPTShellElement(tag = "chatgpt-shell") {
  // SSR-safe guard
  if (!("customElements" in globalThis)) return

  if (!customElements.get(tag)) {
    customElements.define(tag, ChatGPTShellElement)
  }
}
